import React, { useMemo } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const ROUTE_CRUMBS = {
  "/dashboard": {
    label: "Dashboard",
  },
  "/matches": {
    label: (_params, location) => location.state?.breadcrumbLabel || "Matcher",
    parent: "/dashboard",
  },
  "/live": {
    label: "Live",
  },
  "/live/:officialId": {
    label: "Livevy",
    parent: "/live",
  },
  "/account": {
    label: "Mina sidor",
  },
  "/account/matches": {
    label: "Mina protokoll",
    parent: "/account",
  },
  "/account/matches/:tab": {
    label: (_params, location) =>
      location.pathname.endsWith("/saved-protocols")
        ? "Sparade protokoll"
        : "Skapade protokoll",
    parent: "/account/matches",
  },
  "/account/matches/saved/:userMatchId": {
    label: "Matchdetaljer",
    parent: "/account/matches/saved-protocols",
  },
  "/account/profile": {
    label: "Profil",
    parent: "/account",
  },
  "/account/notifications": {
    label: "Notiser",
    parent: "/account",
  },
  "/account/protocol": {
    label: "Tema & visning",
    parent: "/account",
  },
  "/account/appearance": {
    label: "Tema & visning",
    parent: "/account",
  },
  "/account/security": {
    label: "Säkerhet",
    parent: "/account",
  },
  "/account/privacy": {
    label: "Data & sekretess",
    parent: "/account",
  },
};

function resolveMatch(pathname) {
  for (const pattern of Object.keys(ROUTE_CRUMBS)) {
    const m = matchPath({ path: pattern, end: true }, pathname);
    if (m) return { pattern, params: m.params };
  }
  return null;
}

export default function Breadcrumbs({ hideIfNoMatch = true }) {
  const location = useLocation();

  const items = useMemo(() => {
    const match = resolveMatch(location.pathname);
    if (!match) return [];

    const chain = [];
    let cursor = match.pattern;

    while (cursor) {
      const def = ROUTE_CRUMBS[cursor];
      if (!def) break;

      const label =
        typeof def.label === "function"
          ? def.label(match.params, location)
          : def.label;

      const href = cursor === match.pattern ? location.pathname : cursor;
      chain.unshift({ label, href, isCurrent: cursor === match.pattern });
      cursor = def.parent;
    }

    return chain;
  }, [location]);

  if (hideIfNoMatch && items.length === 0) return null;

  return (
    <Breadcrumb className="p-4">
      <BreadcrumbList>
        {items.map((it, i) => {
          const isLast = i === items.length - 1;
          return (
            <React.Fragment key={it.href}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{it.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={it.href}>{it.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
